import { useSyncExternalStore } from "react";
import type { Sensitivity } from "@/lib/frameMonitor";
import { safeLocalStorage } from "@/lib/storage";

// Toggled from the command palette, or by opening any dashboard URL with
// `?debug=fps` (on) or `?debug=off` (off).
const STORAGE_KEY = "bs:perf-debug";
const SENSITIVITY_STORAGE_KEY = "bs:perf-debug-sensitivity";
const POSITION_STORAGE_KEY = "bs:perf-debug-position";

const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

export function isPerfDebugEnabled(): boolean {
  return safeLocalStorage.get(STORAGE_KEY) === "1";
}

export function setPerfDebugEnabled(enabled: boolean): void {
  if (enabled) {
    safeLocalStorage.set(STORAGE_KEY, "1");
  } else {
    safeLocalStorage.remove(STORAGE_KEY);
  }
  notify();
}

export function perfDebugSensitivity(): Sensitivity {
  return safeLocalStorage.get(SENSITIVITY_STORAGE_KEY) === "strict"
    ? "strict"
    : "relaxed";
}

export function setPerfDebugSensitivity(sensitivity: Sensitivity): void {
  safeLocalStorage.set(SENSITIVITY_STORAGE_KEY, sensitivity);
  notify();
}

export type PerfDebugPosition = { x: number; y: number };

export function perfDebugPosition(): PerfDebugPosition | null {
  const raw = safeLocalStorage.get(POSITION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const { x, y } = JSON.parse(raw) as Partial<PerfDebugPosition>;
    return Number.isFinite(x) && Number.isFinite(y)
      ? { x: x as number, y: y as number }
      : null;
  } catch {
    return null;
  }
}

export function setPerfDebugPosition(position: PerfDebugPosition): void {
  safeLocalStorage.set(POSITION_STORAGE_KEY, JSON.stringify(position));
}

export function applyPerfDebugQueryParam(search: string): void {
  const value = new URLSearchParams(search).get("debug");
  if (value === "fps") setPerfDebugEnabled(true);
  if (value === "off") setPerfDebugEnabled(false);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === SENSITIVITY_STORAGE_KEY) {
      listener();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function usePerfDebugEnabled(): boolean {
  return useSyncExternalStore(subscribe, isPerfDebugEnabled, () => false);
}

export function usePerfDebugSensitivity(): Sensitivity {
  return useSyncExternalStore(
    subscribe,
    perfDebugSensitivity,
    (): Sensitivity => "relaxed",
  );
}
